import { createContext, use, useEffect, useMemo } from "react";
import type { JSX, ReactNode } from "react";
import { createTranslator, languageTagFor } from "./translate";
import type { Locale, Translate } from "./translate";

/** Value of the i18n context. */
export interface I18nContextValue {
  /** Current locale. */
  readonly locale: Locale;
  /** BCP 47 tag for `Intl` formatters. */
  readonly languageTag: string;
  /** Translation function. */
  readonly t: Translate;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** Properties of {@link I18nProvider}. */
export interface I18nProviderProps {
  /** Locale to use. */
  readonly locale: Locale;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides translations for the chosen locale and keeps the language of the document in sync, so
 * screen readers pronounce the texts in the chosen language (WCAG 3.1.1).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function I18nProvider(props: I18nProviderProps): JSX.Element {
  const { locale } = props;
  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      languageTag: languageTagFor(locale),
      t: createTranslator(locale),
    }),
    [locale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const provider = <I18nContext value={value}>{props.children}</I18nContext>;

  return provider;
}

/**
 * Returns the i18n context.
 *
 * @returns The context value.
 * @throws Error if no {@link I18nProvider} is present.
 */
export function useI18n(): I18nContextValue {
  const value = use(I18nContext);

  if (value === null) {
    throw new Error("useI18n must be used inside an I18nProvider.");
  }

  return value;
}
